import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { BRANDS } from "app/constants";
import AppGlobalsContext from "app/AppGlobalsContext";
import { SortTrigger, SortDrawer } from "../../../productSort";
import { FilterTrigger, FilterDrawer } from "../../../productFilters";
import { Map } from "../../icons/Map";
import { List } from "../../icons/List";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import MiniSearchContainer from "app/features/SearchEngine/MiniSearch/MiniSearchContainer";
import { useResizeObserver } from "app/hooks";
import classNames from "classnames";
import PropTypes from "prop-types";
import { ActionBarMobileLoader } from "./ActionBarMobileLoader";
import "./ActionBarMobile.scss";

export const ActionBarMobile = ({
	showMiniSearch = false,
	showMapButton = false,
	isSortAvailable = true,
	variant = BUTTON_VARIANTS.STROKE,
	isSticky = false,
	isLoading = true,
	onFilterOpen,
	onFilterPanelOpen,
	onSortOpen,
	onSortChange,
	onApplyFilters,
	onFilterChange,
	onMapClick,
	className,
}) => {
	const { brand } = useContext(AppGlobalsContext);
	const { height: headerHeight } = useResizeObserver({ selector: ".partner-dynamic-header" });
	const { pathname } = useLocation();

	const actionBarClassName = classNames("action-bar-mobile", {
		"action-bar-mobile--variant-stroke": variant === BUTTON_VARIANTS.STROKE,
		"action-bar-mobile--variant-borderless": variant === BUTTON_VARIANTS.BORDERLESS,
		"action-bar-mobile--sticky": isSticky,
		[className]: className,
	});

	const isMapPage = pathname.includes("/map");

	if (isLoading) {
		return (
			<ActionBarMobileLoader
				showSortLoader={isSortAvailable}
				showMiniSearch={showMiniSearch}
				showMapLoader={showMapButton}
			/>
		);
	}

	return (
		<div className={actionBarClassName} style={{ top: brand === BRANDS.HP ? headerHeight : 0 }}>
			{showMiniSearch && <MiniSearchContainer />}

			<div className="action-bar-mobile__buttons">
				<FilterTrigger onClick={onFilterOpen} variant={variant} />
				<FilterDrawer
					onApply={onApplyFilters}
					onChange={onFilterChange}
					onOpenPanel={onFilterPanelOpen}
				/>

				{isSortAvailable && (
					<>
						<SortTrigger onClick={onSortOpen} variant={variant} />
						<SortDrawer onChange={onSortChange} />
					</>
				)}

				{showMapButton && (
					<Button
						variant={variant}
						type="button"
						className="action-bar-mobile__map-button"
						aria-haspopup="dialog"
						aria-expanded="false"
						onClick={onMapClick}
					>
						{isMapPage ? <List /> : <Map />}

						<FormattedMessage
							id={
								isMapPage
									? "product.page.listing.button.label"
									: "product.page.map.button.label"
							}
						/>
					</Button>
				)}
			</div>
		</div>
	);
};

ActionBarMobile.propTypes = {
	showMiniSearch: PropTypes.bool,
	showMapButton: PropTypes.bool,
	isSortAvailable: PropTypes.bool,
	variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
	isSticky: PropTypes.bool,
	isLoading: PropTypes.bool,
	onFilterOpen: PropTypes.func,
	onFilterPanelOpen: PropTypes.func,
	onSortOpen: PropTypes.func,
	onSortChange: PropTypes.func,
	onApplyFilters: PropTypes.func,
	onFilterChange: PropTypes.func,
	onMapClick: PropTypes.func,
	className: PropTypes.string,
};
